import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, UserIcon } from '@heroicons/react/24/outline';
import employeeService from '../../../services/api/employeeService';
import eventService from '../../../services/api/eventService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

/**
 * EventAssigneesTab - Manage employee assignments for tasks/appointments/reminders
 *
 * Note: This tab is NOT shown for meetings (meetings use Invitees in EventDetailsTab)
 *
 * Features:
 * - List current assignees (employees responsible for the task/appointment)
 * - Add new assignees from employee picker
 * - Remove assignees
 * - Set primary assignee
 */
const EventAssigneesTab = ({ event, onUnsavedChanges }) => {
  const [assignees, setAssignees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingAssignee, setIsAddingAssignee] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const isNewEvent = !event?.id;

  // Fetch employees and current assignees
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all employees for picker
        const employeesResponse = await employeeService.getDirectory({ status: 'active' });
        if (employeesResponse.success) {
          setEmployees(employeesResponse.employees || []);
        }

        // If existing event, fetch current assignees
        if (event?.id) {
          const assigneesResponse = await eventService.getAssignees(event.id);
          if (assigneesResponse.success) {
            setAssignees(assigneesResponse.assignees || []);
          }
        } else if (event?.assignees) {
          setAssignees(event.assignees);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  // Handle add assignee
  const handleAddAssignee = async () => {
    if (!selectedEmployeeId || !event?.id) return;

    try {
      setIsAddingAssignee(true);
      const response = await eventService.addAssignee(event.id, selectedEmployeeId, {
        isOrganizer: false, // Creator is the organizer (set by backend for meetings/reminders)
        requiresResponse: event.eventType === 'meeting'
      });

      if (response.success) {
        // Add to local state
        const employee = employees.find(e => e.id === selectedEmployeeId);
        setAssignees(prev => [...prev, {
          ...response.assignee,
          employee: employee
        }]);
        setSelectedEmployeeId('');
        if (onUnsavedChanges) onUnsavedChanges(true);
      }
    } catch (err) {
      console.error('Error adding assignee:', err);
      setError(getApiErrorMessage(err, 'Failed to add assignee'));
    } finally {
      setIsAddingAssignee(false);
    }
  };

  // Handle remove assignee
  const handleRemoveAssignee = async (employeeId) => {
    if (!event?.id) return;

    try {
      await eventService.removeAssignee(event.id, employeeId);
      // Use String comparison to handle integer/string ID mismatches
      setAssignees(prev => prev.filter(a => String(a.employeeId) !== String(employeeId)));
      if (onUnsavedChanges) onUnsavedChanges(true);
    } catch (err) {
      console.error('Error removing assignee:', err);
      setError('Failed to remove assignee');
    }
  };

  // Get employees not yet assigned
  // Use String comparison to handle integer/string ID mismatches
  const availableEmployees = employees.filter(
    emp => !assignees.some(a => String(a.employeeId) === String(emp.id))
  );


  if (isNewEvent) {
    return (
      <div className="p-6 text-center">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Save Event First</h3>
        <p className="mt-1 text-sm text-gray-500">
          Create the event before adding assignees
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Add Assignee */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add Team Member
        </label>
        <div className="flex gap-2">
          <select
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            disabled={availableEmployees.length === 0}
          >
            <option value="">
              {availableEmployees.length === 0
                ? 'All employees assigned'
                : 'Select team member...'}
            </option>
            {availableEmployees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
                {emp.jobTitle ? ` - ${emp.jobTitle}` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={handleAddAssignee}
            disabled={!selectedEmployeeId || isAddingAssignee}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAddingAssignee ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800"></div>
            ) : (
              <PlusIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Current Assignees */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Assigned ({assignees.length})
        </h3>

        {assignees.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <UserIcon className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No team members assigned</p>
          </div>
        ) : (
          <div className="space-y-2">
            {assignees.map((assignee) => {
              // Use String comparison to handle integer/string ID mismatches
              const employee = assignee.employee || employees.find(e => String(e.id) === String(assignee.employeeId));

              return (
                <div
                  key={assignee.employeeId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-xs font-medium">
                      {employee?.firstName?.[0]}{employee?.lastName?.[0]}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {employee?.firstName} {employee?.lastName}
                      </span>
                      {employee?.jobTitle && (
                        <p className="text-xs text-gray-500">{employee.jobTitle}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveAssignee(assignee.employeeId)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove assignee"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info about assignees */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
        <strong>Tip:</strong> Assignees are team members responsible for completing this {event?.eventType || 'event'}.
      </div>
    </div>
  );
};

export default EventAssigneesTab;
